import { PUBLIC_API_URL, PRIVATE_API_URL } from "src/config";
import { ImageFilters } from "./interfaces";
import { getValidToken } from "./services/authService";
import logger from "./logger";

// Constants for API paths to avoid string repetition
const IMAGE_PATH = "/images";
const USER_PATH = "/user";
const FILES_PATH = "/files";
const FEEDBACK_PATH = "/feedback";
const PRESIGNEDURL_PATH = "/presignedurl"; // Matching the private API construct: /presignedurl/{key}
const COMPETITION_PATH = "/competitions";
const CHECKOUT_PATH = "/checkout"; // For public checkout calls

// Enum for HTTP methods
enum HttpMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
}

// User interface
interface User {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly registrationDate?: string; // ISO date string when user registered
}

// API call options interface
interface ApiCallOptions {
  method?: HttpMethod;
  body?: any;
  authToken?: string;
  userId?: string;
}

// Centralized error handling helper
const handleError = (errorMessagePrefix: string, response: Response, errorBody: string | null) => {
  const errorMessage = errorBody || response.statusText;
  logger.error('error', `${errorMessagePrefix}: HTTP error! Status: ${response.status} - ${errorMessage}`);
  throw new Error(`${errorMessagePrefix}: HTTP error! Status: ${response.status} - ${errorMessage}`);
};

// Generic API call function (internal)
const callApi = async <T = any>(
  endpoint: string,
  errorMessagePrefix: string,
  options: ApiCallOptions
): Promise<T | null> => {
  if (!endpoint) {
    logger.error('error', `${errorMessagePrefix}: No endpoint provided`);
    return null;
  }
  logger.info('info', `Fetching data from ${endpoint}`);
  const { method = HttpMethod.GET, body, authToken, userId } = options;
  try {
    const headers: Record<string, string> = {
      ...(body && { 'Content-Type': 'application/json' }),
    };
    if (authToken) {
      try {
        const freshToken = await getValidToken();
        headers['Authorization'] = `Bearer ${freshToken}`;
      } catch (error) {
        logger.info('error', 'Token refresh failed:', error);
        throw new Error('TokenRefreshFailed');
      }
    }
    if (userId) headers['UserId'] = userId;
    const fetchOptions: RequestInit = {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    };
    logger.info('info', 'Using fetch options:', fetchOptions);
    logger.info('info', `Making ${method} request to: ${endpoint}`);
    const response = await fetch(endpoint, fetchOptions);
    logger.info('info', `Response received: ${response.status} ${response.statusText}`);
    if (response.status === 401) {
      logger.info('error', 'Unauthorized request, token might be invalid');
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      let errorBodyText: string | null = null;
      try {
        errorBodyText = await response.text();
      } catch (e) {/* Ignore */}
      logger.error('error', `HTTP error response: ${response.status} ${response.statusText}`, errorBodyText);
      handleError(errorMessagePrefix, response, errorBodyText);
      return null;
    }
    if (response.status === 204) {
      logger.info('info', `Received 204 No Content for ${method} ${endpoint}`);
      return null;
    }
    const contentLength = response.headers.get('content-length');
    if (contentLength === '0') {
      logger.info('info', `Received empty body (Content-Length: 0) for ${method} ${endpoint}`);
      return null;
    }
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      logger.info('info', `Received JSON response for ${method} ${endpoint}, parsing...`);
      try {
        return await response.json() as T;
      } catch (jsonError) {
        logger.error('error', `Failed to parse JSON response for ${method} ${endpoint}:`, jsonError);
        throw new Error(`${errorMessagePrefix}: Failed to parse expected JSON response. Status: ${response.status}`);
      }
    } else {
      logger.info('info', `Received non-JSON response (Content-Type: ${contentType}) for ${method} ${endpoint}. Assuming success with no data.`);
      return null;
    }
  } catch (error) {
    if (error instanceof Error) {
      logger.error('error', `${errorMessagePrefix} failed (${error.name}):`, error.message, error.stack);
    } else {
      logger.error('error', `${errorMessagePrefix} failed:`, error);
    }
    throw error;
  }
};

// Private API fetcher (uses PRIVATE_API_URL)
const privateApiFetcher = async (
  authToken: string,
  userId: string,
  endpointPath: string,
  method: HttpMethod = HttpMethod.GET,
  body?: any
) => {
  const endpoint = `${PRIVATE_API_URL}${endpointPath}`;
  return callApi(endpoint, 'Private API Call', { method, authToken, body, userId });
};

// Public API fetcher (uses PUBLIC_API_URL)
const publicApiFetcher = async (
  endpointPath: string,
  method: HttpMethod = HttpMethod.GET,
  body?: any
) => {
  const endpoint = `${PUBLIC_API_URL}${endpointPath}`;
  logger.info('info', `Public API fetcher called with endpoint: ${endpoint}, method: ${method}`);
  return callApi(endpoint, 'Public API Call', { method, body }); // No authToken or userId for public calls by default
};

// Helper function to build query string
const buildQueryString = (filters: ImageFilters): string => {
  const params = new URLSearchParams();
  if (filters.species) params.append('species', filters.species);
  if (filters.category) params.append('category', filters.category);
  if (filters.locationOnly) params.append('locationOnly', 'true');
  if (filters.userId) params.append('userId', filters.userId);
  if (filters.competitionId) params.append('competitionId', filters.competitionId);
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

// --- PRIVATE API Calls ---
export const getImageWithAuth = (authToken: string, userId: string, imageId: string) =>
  privateApiFetcher(authToken, userId, `${IMAGE_PATH}/${imageId}`);

export const getStripeDashboardUrl = async (authToken: string, userId: string) =>
  privateApiFetcher(authToken, userId, `${USER_PATH}/create-stripe-dashboard`);

export const voteForImage = (authToken: string, userId: string, imageId: string, competitionId: string) =>
  privateApiFetcher(authToken, userId, `${IMAGE_PATH}/${imageId}/vote?competitionId=${competitionId}`, HttpMethod.PUT);

export const userHasVoted = (authToken: string, userId: string, competitionId: string) =>
  privateApiFetcher(authToken, userId, `${IMAGE_PATH}/user/${userId}/voted?competitionId=${competitionId}`, HttpMethod.GET);

export const getUserImages = (authToken: string, userId: string) =>
  privateApiFetcher(authToken, userId, `${IMAGE_PATH}/user/${userId}`);

export const getUserImagesWithCompetitionStatus = (authToken: string, userId: string, competitionId: string) =>
  privateApiFetcher(authToken, userId, `${IMAGE_PATH}/user/${userId}/competition?competitionId=${competitionId}`);

export const enterCompetition = (authToken: string, userId: string, imageId: string, competitionId: string) =>
  privateApiFetcher(authToken, userId, `${IMAGE_PATH}/${imageId}/enter?competitionId=${competitionId}`, HttpMethod.PUT);

export const getUser = (authToken: string, userId: string) =>
  privateApiFetcher(authToken, userId, USER_PATH);

export const submitUserData = (authToken: string, userId: string, user: User) =>
  privateApiFetcher(authToken, userId, USER_PATH, HttpMethod.POST, user);

export const deleteImage = (authToken: string, userId: string, imageId: string) =>
  privateApiFetcher(authToken, userId, `${IMAGE_PATH}/${imageId}`, HttpMethod.DELETE);

export const saveImage = (authToken: string, userId: string, imageId: string, body: any) =>
  privateApiFetcher(authToken, userId, `${IMAGE_PATH}/create`, HttpMethod.POST, body);

export const updateImage = (authToken: string, userId: string, imageId: string, body: any) =>
  privateApiFetcher(authToken, userId, `${IMAGE_PATH}/${imageId}`, HttpMethod.POST, body);

export const getSales = (authToken: string, userId: string) =>
  privateApiFetcher(authToken, userId, `${USER_PATH}/sales`);

export const withdraw = (authToken: string, userId: string, amount: number) =>
  privateApiFetcher(authToken, userId, `${USER_PATH}/withdraw`, HttpMethod.POST, { amount });

export const getWithdrawals = (authToken: string, userId: string) =>
  privateApiFetcher(authToken, userId, `${USER_PATH}/withdrawals`);

export const checkProcessingStatus = async (authToken: string, userId: string, fileId: string) => {
  const statusData = await privateApiFetcher(authToken, userId, `${FILES_PATH}/${fileId}/status`);
  return statusData?.status;
};

export const getPresignedUrl = (authToken: string, userId: string, fileName: string, id: string) =>
  privateApiFetcher(authToken, userId, `${PRESIGNEDURL_PATH}/${fileName}?id=${id}`);

export const getAccountBalance = (authToken: string, userId: string) =>
  privateApiFetcher(authToken, userId, `${USER_PATH}/balance`);

export const createStripeAccount = (authToken: string, userId: string) =>
  privateApiFetcher(authToken, userId, `${USER_PATH}/create-stripe-account`, HttpMethod.POST, { userId });

export const stripeOnboardingComplete = (authToken: string, userId: string, stripeAccountId: string) =>
  privateApiFetcher(authToken, userId, `${USER_PATH}/stripe-onboarding-complete`, HttpMethod.POST, { userId, stripeAccountId });

export const removeCompetitionEntry = async (accessToken: string, userId: string, imageId: string, competitionId: string): Promise<void> => {
  await privateApiFetcher(accessToken, userId, `${COMPETITION_PATH}/entry?imageId=${imageId}&competitionId=${competitionId}`, HttpMethod.DELETE);
};

// --- PUBLIC API Calls ---
export const getImageBasic = (imageId: string) =>
  publicApiFetcher(`${IMAGE_PATH}/public/${imageId}`);

export const getImagesPublic = (filters?: ImageFilters) => {
  const queryString = filters ? buildQueryString(filters) : '';
  return publicApiFetcher(`${IMAGE_PATH}/public${queryString}`);
};

export const getImageVotes = (filters?: ImageFilters) => {
  const queryString = filters ? buildQueryString(filters) : '';
  return publicApiFetcher(`${IMAGE_PATH}/votes${queryString}`);
};

export const getCompetitions = () =>
  publicApiFetcher(COMPETITION_PATH);

export const hasUserEnteredCompetition = (userId: string, competitionId: string) => // This was public in original, assuming it remains so
  publicApiFetcher(`${IMAGE_PATH}/entered/${userId}?competitionId=${competitionId}`);

export const getStripeAccountId = (imageId: string) => // Assuming public, was in original public section
  publicApiFetcher(`${IMAGE_PATH}/${imageId}/stripe`);

export const submitFeedback = (body: any) =>
  publicApiFetcher(FEEDBACK_PATH, HttpMethod.POST, body);

export const callVerifyPurchase = (sessionId: string, connectedAccountId: string) => // Assuming public
  publicApiFetcher(`${IMAGE_PATH}/purchase/${sessionId}/${connectedAccountId}`);

export const getUserCount = () =>
  publicApiFetcher(`${USER_PATH}/count`);

export const callCreateCheckout = async (imageId: string, stripeAccountId: string) =>
  publicApiFetcher(`${CHECKOUT_PATH}/create/${imageId}/${stripeAccountId}`, HttpMethod.POST);

export const getCompetitionEntry = async (userId: string, competitionId: string): Promise<{ imageId: string } | null> => {
  try {
    const data = await publicApiFetcher(`${IMAGE_PATH}/entered/${userId}?competitionId=${competitionId}`, HttpMethod.GET);
    return data;
  } catch (error) {
    if (error instanceof Error && error.message.includes('404')) {
      return null;
    }
    logger.error('Error fetching public competition entry:', error);
    throw error;
  }
};

// Utility function for direct file uploads (e.g., to S3 presigned URL)
export const putFile = async (url: string, headers: Record<string, string>, body: any) => {
  const response = await fetch(url, { 
    method: HttpMethod.PUT, 
    headers,
    body: body
  });
  return response;
}